/**
 * Happy Cats Guarantee storefront wizard.
 *
 * A vanilla-JS, mobile-first wizard mounted into #happy-cats-app. It reads its
 * configuration from the element's data- attributes (set by
 * sections/happy-cats-app.liquid) and talks to the standalone happy-cats-server
 * Vercel project.
 *
 * This file ports the verified design in ux/wizard-reference.html faithfully:
 * the brandbar, flow rail, lookup, product picker, the three doors, and the
 * review / report / not-eating paths with every loading, validation, rejection,
 * confirmation, and recovery state. The design is reproduced exactly; the API
 * behavior is preserved exactly.
 *
 * Flow (API contract):
 *   1. Find order      POST {endpoint_base}/lookup  { order_number, contact, website }
 *   2. Pick product    (skipped when the order has a single line item)
 *   3. Pick a path     review | problem_claim | not_eating
 *   4. Path form        POST {endpoint_base}/submissions  (multipart)
 *                       with a per-attempt idempotency_key (crypto.randomUUID),
 *                       minted ONCE per path and reused across retries.
 *   5. Confirmation     tailored per path:
 *        review (>=4)  -> review_url + referral_url
 *        review (<=3)  -> soft signal logged, offer the report / not-eating paths
 *        problem_claim -> reassurance hero, make-good promise; photo required for
 *                         quality reasons
 *        not_eating    -> coaching guide + WhatsApp + Recharge portal (pause) links
 *
 * Copy obeys the Sprouties brand rules: no em dashes, "box" not "tray",
 * "nibbling" not "grazing", no puns, no fear or guilt, benefits before features.
 *
 * No framework, no build step. One IIFE, no globals leaked.
 */
(function () {
  'use strict';

  var root = document.getElementById('happy-cats-app');
  if (!root) return;

  // -------------------------------------------------------------------------
  // Configuration (from the section's data- attributes)
  // -------------------------------------------------------------------------

  var CONFIG = {
    endpointBase: stripTrailingSlash(root.getAttribute('data-endpoint-base') || ''),
    whatsappNumber: (root.getAttribute('data-whatsapp-number') || '').replace(/[^0-9]/g, ''),
    reviewUrl: (root.getAttribute('data-review-url') || '').trim(),
    referralUrl: (root.getAttribute('data-referral-url') || '').trim(),
    rechargePortalUrl: (root.getAttribute('data-recharge-portal-url') || '').trim(),
    // HUB EMBED SAFETY: the Customer Hub owns issue/replacement semantics. When
    // this flag is present the problem path emits a local handoff event instead
    // of POSTing a second claim to the standalone Happy Cats service. Review and
    // not-eating paths keep using the standalone service unchanged.
    hubIssueBridge: root.getAttribute('data-hub-issue-bridge') === '1',
    // SESSION MODE (Wellness Hub): when the embedding page already knows the
    // signed-in customer (their latest order number + the email on their
    // account), it passes both and the wizard skips the lookup form entirely -
    // it runs the SAME server-verified lookup automatically and lands on the
    // "Found it" confirmation (which keeps "Look up a different order" as the
    // wrong-order escape). Any miss falls back to the manual form, prefilled.
    autoOrder: (root.getAttribute('data-auto-order') || '').replace(/^#/, '').trim(),
    autoContact: (root.getAttribute('data-auto-contact') || '').trim()
  };

  // Raw selection cap (a memory guard only). The photo is compressed to a small
  // JPEG before upload, so the file the customer picks can be large; what we POST
  // is not. See preparePhoto.
  var MAX_PHOTO_BYTES = 25 * 1024 * 1024;
  // A JPG/PNG already at or under this uploads untouched; anything larger (or any
  // HEIC/HEIF/WebP) is re-encoded to a JPEG under this size, keeping the whole
  // multipart body under the Vercel Edge 4MB request-body limit.
  var UPLOAD_TARGET_BYTES = 3.5 * 1024 * 1024;
  var SERVER_MAX_BYTES = 4 * 1024 * 1024; // the server's hard JPG/PNG cap
  var MAX_EDGE_DIM = 2000;                 // longest side after downscale
  // LOAD-BEARING: many browsers report an empty file.type for HEIC (the common
  // UAE-iPhone case), so an empty type must pass the gate (see acceptablePhotoType).
  // We accept HEIC/HEIF/WebP at selection and convert them to a server-accepted
  // JPEG before upload, so the server only ever receives the JPG/PNG it allows.
  var OK_TYPE = /^image\/(jpe?g|png|heic|heif|webp)$/i;

  // Problem reasons that show the box and require a photo before a make-good can
  // run. Ids mirror the server's QUALITY_ISSUES list exactly. "something_else"
  // is a problem_claim too but is open-ended and needs no photo.
  var QUALITY_ISSUES = ['short_pale', 'smell_mold', 'bugs', 'arrived_bad', 'wrong_box'];

  // Report reason chips, grouped exactly like the reference ("How it looks" /
  // "How it arrived"). Each chip maps to one exact issue_type enum value.
  var ISSUE_GROUPS = [
    {
      label: 'How it looks',
      options: [
        { id: 'short_pale', label: 'Short or pale grass' },
        { id: 'smell_mold', label: 'Smell or mold' },
        { id: 'bugs', label: 'Bugs in the box' }
      ]
    },
    {
      label: 'How it arrived',
      options: [
        { id: 'arrived_bad', label: 'Arrived warm, late, or damaged' },
        { id: 'wrong_box', label: 'Wrong box' },
        { id: 'something_else', label: 'Something else' }
      ]
    }
  ];

  // The rebuilt report uses one multi-select question. These are display options
  // only: the submit sends issue_type 'something_else' (manual review, no auto
  // make-good) with the ticked labels plus the required note in the text the team
  // reads, so every claim stays pending our team's review. 'something_else' is
  // exclusive: ticking it clears the rest, and it asks for a longer note.
  var REPORT_OPTIONS = [
    { id: 'pale_yellow',    label: 'Grass looks pale or yellow' },
    { id: 'short_grown',    label: 'Grass is short or barely grown' },
    { id: 'wilted_drying',  label: 'Grass arrived wilted or drying' },
    { id: 'crushed',        label: 'Box was crushed or damaged' },
    { id: 'wrong_box',      label: 'Wrong box or grass type' },
    { id: 'missing',        label: 'Part of my order is missing' },
    { id: 'something_else', label: 'Something else', exclusive: true }
  ];

  var LOVE_CHIPS = [
    'Fresh and green',
    'Went straight for it',
    'Fewer hairballs',
    'Tall grass',
    'Lasted well'
  ];

  // The reaction word per star count: warm, never gushing. 2 and 3 stars are
  // differentiated by one notch of warmth (design-critique fix 3).
  var STAR_WORDS = [
    '',
    'Thank you for telling us, let us put this right',
    'Thank you, we want to do better for you',
    'Thank you, we want the next box to be better',
    'Lovely to hear',
    'Wonderful, thank you'
  ];

  // Grass metadata: title, benefit line (benefits before features), and icon key.
  var GRASS = {
    wheat:  { label: 'Wheat box',  meta: 'Soft, fine grass · a daily ritual',     icon: 'wheat' },
    oat:    { label: 'Oat box',    meta: 'Crisp grass · for particular eaters', icon: 'oat' },
    barley: { label: 'Barley box', meta: 'Broad leaves · more in every bite',       icon: 'barley' },
    mixed:  { label: 'Mixed box',  meta: 'Freshly grown, organic, delivered across the UAE',   icon: 'grass' },
    other:  { label: 'Your box',   meta: 'Freshly grown, organic, delivered across the UAE',   icon: 'grass' }
  };

  // Real Sprouties product photography for the screen hero, keyed by grass. Before
  // a box is known we lead with the wheat shot. These are the storefront CDN
  // images, so the hero always matches what the customer actually bought.
  var HERO_PHOTO = {
    wheat:  'https://sprouties.ae/cdn/shop/files/fresh-wheat-cat-grass-5521777.jpg?width=900',
    oat:    'https://sprouties.ae/cdn/shop/files/fresh-oat-cat-grass-3626306.jpg?width=900',
    barley: 'https://sprouties.ae/cdn/shop/files/fresh-barley-cat-grass-6045128.jpg?width=900',
    mixed:  'https://sprouties.ae/cdn/shop/files/fresh-wheat-cat-grass-5521777.jpg?width=900',
    other:  'https://sprouties.ae/cdn/shop/files/fresh-wheat-cat-grass-5521777.jpg?width=900'
  };
  function heroImageUrl() {
    // The real product photo for the box the customer actually bought, straight
    // from the storefront. Falls back to a grass shot only before a box is known.
    if (state.lineItem && state.lineItem.image) return state.lineItem.image;
    var key = (state.lineItem && state.lineItem.grass) || 'wheat';
    return HERO_PHOTO[key] || HERO_PHOTO.wheat;
  }

  // -------------------------------------------------------------------------
  // Wizard state
  // -------------------------------------------------------------------------

  var state = {
    token: null,
    order: null, // { name, first_name, placed_at, is_subscriber, line_items: [] }
    lineItem: null, // the selected line item object
    path: null, // 'review' | 'problem_claim' | 'not_eating'
    submitting: false,
    autoEntered: false, // session mode: lookup ran automatically for the signed-in customer
    idempotencyKeys: {} // path -> idempotency_key, minted once, reused across retries
  };

  // -------------------------------------------------------------------------
  // Small helpers
  // -------------------------------------------------------------------------

  function stripTrailingSlash(url) {
    return url.replace(/\/+$/, '');
  }

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (key) {
        var value = attrs[key];
        if (value == null || value === false) return;
        if (key === 'class') {
          node.className = value;
        } else if (key === 'text') {
          node.textContent = value;
        } else if (key === 'html') {
          node.innerHTML = value;
        } else if (key.indexOf('on') === 0 && typeof value === 'function') {
          node.addEventListener(key.slice(2).toLowerCase(), value);
        } else if (value === true) {
          node.setAttribute(key, '');
        } else {
          node.setAttribute(key, value);
        }
      });
    }
    appendChildren(node, children);
    return node;
  }

  function appendChildren(node, children) {
    if (children == null) return;
    if (!Array.isArray(children)) children = [children];
    children.forEach(function (child) {
      if (child == null || child === false) return;
      if (typeof child === 'string') {
        node.appendChild(document.createTextNode(child));
      } else {
        node.appendChild(child);
      }
    });
  }

  function clear(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  // Build an inline SVG from a viewBox + path data. Decorative by default
  // (aria-hidden), so meaning is carried by adjacent text or an aria-label.
  function icon(paths, opts) {
    opts = opts || {};
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', opts.viewBox || '0 0 24 24');
    if (opts.fill === 'none') {
      svg.setAttribute('fill', 'none');
      svg.setAttribute('stroke', 'currentColor');
      svg.setAttribute('stroke-width', opts.stroke || '2.2');
      if (opts.linecap) { svg.setAttribute('stroke-linecap', 'round'); svg.setAttribute('stroke-linejoin', 'round'); }
    } else {
      svg.setAttribute('fill', 'currentColor');
    }
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    (Array.isArray(paths) ? paths : [paths]).forEach(function (d) {
      var p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      p.setAttribute('d', d);
      svg.appendChild(p);
    });
    return svg;
  }

  // Icon library (decorative inline SVGs, mirroring the reference).
  var ICON = {
    leaf:    function () { return icon('M11.2 27c-.5-3.3-.7-6.3-.6-9-1.3-.5-2.4-1.4-3.2-2.6-1.1-1.7-1.6-3.8-1.6-6 1.7.7 3 1.9 3.9 3.5.4.7.7 1.5.9 2.3 0-3.4.2-6.7.5-9.7.3 3 .5 6.3.5 9.7.2-.8.5-1.6.9-2.3.9-1.6 2.2-2.8 3.9-3.5 0 2.2-.5 4.3-1.6 6-.8 1.2-1.9 2.1-3.2 2.6.1 2.7-.1 5.7-.6 9z', { viewBox: '0 0 24 28' }); },
    blade:   function () { return icon('M12 3c-1 3-3 4-5 5 0 5 2.5 8 5 9 2.5-1 5-4 5-9-2-1-4-2-5-5z'); },
    sprout:  function () { return icon('M12 3c-1 3-3 4-5 5 0 5 2.5 8 5 9 2.5-1 5-4 5-9-2-1-4-2-5-5zm0 4.5c.7 1.3 1.7 2 2.8 2.6-.2 3-1.6 4.9-2.8 5.6-1.2-.7-2.6-2.6-2.8-5.6 1.1-.6 2.1-1.3 2.8-2.6z'); },
    star:    function () { return icon('M12 2l2.9 6.1 6.6.9-4.8 4.6 1.2 6.6L12 18.6 6.1 20.8l1.2-6.6L2.5 9l6.6-.9z'); },
    shield:  function () { return icon('M12 2L2 7v6c0 5 4 8 10 9 6-1 10-4 10-9V7L12 2zm-1 13l-4-4 1.4-1.4L11 12.2l4.6-4.6L17 9l-6 6z'); },
    badge:   function () { return icon('M12 2l2.4 5 5.6.8-4 3.9 1 5.5L12 19.8 6.9 22.2l1-5.5L4 12.8 9.6 12z'); },
    camera:  function () { return icon('M9 3h6l1 2h3v4H5V5h3l1-2zm3 4a5 5 0 100 10 5 5 0 000-10zm0 2a3 3 0 110 6 3 3 0 010-6z'); },
    chevron: function () { return icon('M9 6l6 6-6 6', { fill: 'none', stroke: '2.4', linecap: true }); },
    chevronL:function () { return icon('M15 6l-6 6 6 6', { fill: 'none', stroke: '2.2', linecap: true }); },
    whatsapp:function () { return icon('M12 2a10 10 0 00-8.6 15l-1.3 4.7 4.8-1.3A10 10 0 1012 2zm0 2a8 8 0 11-4.2 14.8l-.4-.2-2.7.7.7-2.6-.2-.4A8 8 0 0112 4z'); },
    info:    function () { return icon('M12 2a10 10 0 100 20 10 10 0 000-20zm0 5a1.3 1.3 0 011.3 1.3v5.4a1.3 1.3 0 01-2.6 0V8.3A1.3 1.3 0 0112 7zm0 11.5a1.4 1.4 0 110-2.8 1.4 1.4 0 010 2.8z'); },
    retry:   function () { return icon('M20 11A8 8 0 105 18.5M20 5v6h-6', { fill: 'none', stroke: '2.2', linecap: true }); },
    check:   function () { return icon('M5 12l5 5 9-11', { fill: 'none', stroke: '3', linecap: true }); },
    checkBold:function(){ return icon('M9 16.2l-3.5-3.5L4 14.2l5 5 11-11-1.4-1.4z'); },
    heart:   function () { return icon('M12 21s-7.5-4.6-10-9.2C.4 8.6 2 5 5.5 5c2 0 3.4 1.1 4.5 2.6C11.1 6.1 12.5 5 14.5 5 18 5 19.6 8.6 22 11.8 19.5 16.4 12 21 12 21z'); },
    pause:   function () { return icon('M9 5h3v14H9zM15 5h-3v14h3z'); },
    photo:   function () { return icon('M4 5h16a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V6a1 1 0 011-1zm1.5 12.5h13l-4-5-3 3.5-2-2.2zM8 10a1.6 1.6 0 100-3.2A1.6 1.6 0 008 10z'); }
  };

  function grassFor(li) {
    var key = li && li.grass;
    return GRASS[key] || GRASS.other;
  }

  function acceptablePhotoType(f) {
    if (!f.type) return true; // empty type passes (HEIC on many browsers)
    return OK_TYPE.test(f.type);
  }

  // Prepare a photo for upload: downscale + re-encode to a JPEG that stays well
  // under the Edge 4MB request-body limit, and convert HEIC/HEIF/WebP from a phone
  // into the JPEG the server accepts. A small JPG/PNG passes through untouched.
  //
  // Resolves to a File (or null when there is no photo / an optional photo could
  // not be read). Rejects only when a REQUIRED photo cannot be read at all (e.g. a
  // HEIC opened in a browser that cannot decode it), so the caller can ask for a
  // JPG/PNG instead of silently dropping required proof.
  function preparePhoto(file, required) {
    return new Promise(function (resolve, reject) {
      if (!file) { resolve(null); return; }
      var type = (file.type || '').toLowerCase();
      var isJpgPng = type === 'image/jpeg' || type === 'image/png';
      if (isJpgPng && file.size <= UPLOAD_TARGET_BYTES) { resolve(file); return; }

      var done = false;
      var url = null;
      function cleanup() { if (url) { try { URL.revokeObjectURL(url); } catch (e) {} url = null; } }
      function settleFail() {
        if (done) return;
        cleanup();
        // A JPG/PNG that merely failed to re-encode can still upload as-is if it is
        // within the server's cap. Otherwise: reject when required, drop when not.
        if (isJpgPng && file.size <= SERVER_MAX_BYTES) { done = true; resolve(file); return; }
        done = true;
        if (required) reject(new Error('photo_unreadable')); else resolve(null);
      }

      var img = new Image();
      img.onload = function () {
        try {
          var w = img.naturalWidth || img.width;
          var h = img.naturalHeight || img.height;
          if (!w || !h) { settleFail(); return; }
          var scale = Math.min(1, MAX_EDGE_DIM / Math.max(w, h));
          var cw = Math.max(1, Math.round(w * scale));
          var ch = Math.max(1, Math.round(h * scale));
          var canvas = document.createElement('canvas');
          canvas.width = cw; canvas.height = ch;
          var ctx = canvas.getContext && canvas.getContext('2d');
          if (!ctx || !canvas.toBlob) { settleFail(); return; }
          ctx.drawImage(img, 0, 0, cw, ch);
          // Step the JPEG quality down until the encoded blob is under the upload
          // TARGET (3.5MB), so the whole multipart body stays comfortably under the
          // Edge 4MB request limit. Only if even the lowest quality cannot get under
          // the target do we accept a larger blob, and only when it is still within
          // the server cap; otherwise fail so a required photo is never silently lost.
          var qualities = [0.82, 0.7, 0.58, 0.45];
          var encodeAt = function (qi) {
            canvas.toBlob(function (blob) {
              if (done) { cleanup(); return; }
              if (!blob) { settleFail(); return; }
              if (blob.size > UPLOAD_TARGET_BYTES && qi < qualities.length - 1) {
                encodeAt(qi + 1);
                return;
              }
              if (blob.size > SERVER_MAX_BYTES) { settleFail(); return; }
              cleanup();
              done = true;
              var base = (file.name || 'photo').replace(/\.[^.]+$/, '') || 'photo';
              resolve(new File([blob], base + '.jpg', { type: 'image/jpeg' }));
            }, 'image/jpeg', qualities[qi]);
          };
          encodeAt(0);
        } catch (e) { settleFail(); }
      };
      img.onerror = function () { settleFail(); };
      try { url = URL.createObjectURL(file); img.src = url; } catch (e) { settleFail(); }
    });
  }

  function whatsappLink(message) {
    if (!CONFIG.whatsappNumber) return '';
    var base = 'https://wa.me/' + CONFIG.whatsappNumber;
    if (message) base += '?text=' + encodeURIComponent(message);
    return base;
  }

  function uuid() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    if (window.crypto && window.crypto.getRandomValues) {
      var b = new Uint8Array(16);
      window.crypto.getRandomValues(b);
      b[6] = (b[6] & 0x0f) | 0x40;
      b[8] = (b[8] & 0x3f) | 0x80;
      var hex = [];
      for (var i = 0; i < 16; i++) hex.push((b[i] + 0x100).toString(16).slice(1));
      return (
        hex.slice(0, 4).join('') + '-' + hex.slice(4, 6).join('') + '-' +
        hex.slice(6, 8).join('') + '-' + hex.slice(8, 10).join('') + '-' +
        hex.slice(10, 16).join('')
      );
    }
    return 'hc-' + Date.now() + '-' + Math.random().toString(16).slice(2);
  }

  function bytes(n) {
    if (n < 1024) return n + ' B';
    if (n < 1048576) return (n / 1024).toFixed(0) + ' KB';
    return (n / 1048576).toFixed(1) + ' MB';
  }

  function setToArray(set) {
    var out = [];
    set.forEach(function (v) { out.push(v); });
    return out;
  }

  function openExternal(url) {
    if (!url) return;
    try {
      var w = window.open(url, '_blank', 'noopener');
      if (!w) window.location.href = url;
    } catch (e) {
      window.location.href = url;
    }
  }

  // In Customer Hub embed mode, issue/replacement ownership belongs to the Hub's
  // canonical action layer. The event is cancelable: care.js prevents default
  // only after it has matched the server-verified Happy Cats order name to the
  // signed-in customer's Liquid order id. Regardless of listener availability,
  // embed mode always returns true and therefore NEVER renders or submits the
  // standalone problem_claim form. An unhandled bridge fails closed to a support
  // fallback rather than risking a second make-good in an unproven external app.
  function handoffProblemToHub() {
    if (!CONFIG.hubIssueBridge) return false;
    var handled = false;
    try {
      var detail = {
        orderName: orderName(),
        lineItemId: state.lineItem && state.lineItem.id != null ? String(state.lineItem.id) : '',
        lineItemTitle: state.lineItem ? (cleanTitle(state.lineItem.title) || '') : ''
      };
      var ev = new CustomEvent('wh:happy-cats-issue', {
        bubbles: true,
        cancelable: true,
        detail: detail
      });
      root.dispatchEvent(ev);
      handled = ev.defaultPrevented;
    } catch (e) {
      handled = false;
    }
    if (!handled) renderHubIssueBridgeUnavailable();
    return true;
  }

  // -------------------------------------------------------------------------
  // Network
  // -------------------------------------------------------------------------

  function postLookup(orderNumber, contact) {
    return fetch(CONFIG.endpointBase + '/lookup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ order_number: orderNumber, contact: contact, website: '' })
    }).then(readJsonResponse);
  }

  function postSubmission(formData) {
    return fetch(CONFIG.endpointBase + '/submissions', {
      method: 'POST',
      body: formData
    }).then(readJsonResponse);
  }

  function readJsonResponse(res) {
    return res
      .json()
      .catch(function () { return {}; })
      .then(function (body) {
        return { status: res.status, ok: res.ok, body: body || {} };
      });
  }

  // One warm, generic message for any lookup miss: never reveals which detail
  // missed (security by design), and names only the support door this install
  // can actually open.
  var GENERIC_ERROR = CONFIG.whatsappNumber
    ? 'We could not match those details. Check the order number from your box label or confirmation email, and the email or phone used on the order. If it still will not find your box, message us and we will look it up for you.'
    : 'We could not match those details. Check the order number from your box label or confirmation email, and the email or phone used on the order. If it still will not find your box, open Account and help for support.';
  var NETWORK_ERROR =
    'We could not reach our team just now. Check your connection and try again in a moment.';

  // -------------------------------------------------------------------------
  // Shell rendering
  // -------------------------------------------------------------------------

  var screenHost; // the element screens render into

  function mount() {
    clear(root);
    root.classList.add('happy-cats-app--ready');

    if (!CONFIG.endpointBase) {
      screenHost = el('div', { class: 'hc', 'data-active': 'true' });
      root.appendChild(screenHost);
      var setupHelp = CONFIG.whatsappNumber
        ? linkButton('Message us on WhatsApp', whatsappLink('Hi Sprouties, I scanned the box QR.'), 'primary', ICON.whatsapp())
        : accountHelpButton();
      setScreen(
        screenScaffold({
          eyebrow: 'We are getting set up',
          heading: 'Almost ready',
          sub: CONFIG.whatsappNumber
            ? 'This page is not connected yet. Message us on WhatsApp and we will help right away.'
            : 'This page is not connected yet. Open Account and help for support.',
          body: [actions([setupHelp])]
        })
      );
      return;
    }

    screenHost = el('div', { class: 'hc' });
    screenHost.setAttribute('data-active', 'true');
    root.appendChild(screenHost);
    if (CONFIG.autoOrder && CONFIG.autoContact) {
      renderAutoLookup();
    } else {
      renderLookup();
    }
  }

  // Swap in a freshly built screen, re-trigger the entrance + sway, scroll up.
  function setScreen(node) {
    clear(screenHost);
    screenHost.appendChild(node);

    var screen = node.querySelector('.hc-screen');
    if (screen) {
      screen.classList.remove('is-enter');
      void screen.offsetWidth; // force reflow so the animation restarts
      screen.classList.add('is-enter');
    }
    var leaf = node.querySelector('.hc-brandbar__leaf');
    if (leaf) {
      leaf.classList.remove('is-sway');
      void leaf.offsetWidth;
      leaf.classList.add('is-sway');
    }

    if (typeof screenHost.scrollIntoView === 'function') {
      try { screenHost.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }
      catch (e) { screenHost.scrollIntoView(); }
    }
  }

  function setPath(path) {
    // Drives the per-path ambient glow + the report reassurance exemption.
    if (path === 'review') screenHost.setAttribute('data-path', 'review');
    else if (path === 'problem_claim') screenHost.setAttribute('data-path', 'problem');
    else if (path === 'not_eating') screenHost.setAttribute('data-path', 'eating');
    else screenHost.removeAttribute('data-path');
  }

  // The screen hero: a real Sprouties box photo with the guarantee name set into
  // it. Every screen scaffold places this above the lifted content card, so the
  // brand and the product lead, with no icons. The sr-only h1 lives in the section
  // markup so each screen's primary line stays an h2 below it.
  function brandbar() {
    var hero = el('div', { class: 'hc-hero' });
    hero.style.backgroundImage = 'url("' + heroImageUrl() + '")';
    hero.appendChild(el('span', { class: 'hc-hero__label', text: 'Happy Cats Guarantee' }));
    return hero;
  }

  // The flow rail. step: 1 = find, 2 = box, 3 = how-is-it. showAnchor adds the
  // order box context once it is known.
  function rail(step, showAnchor) {
    var labels = ['Find', 'Box', 'How is it'];
    var ariaByStep = {
      1: 'Step 1 of 3: find your box',
      2: 'Step 2 of 3: pick the box',
      3: 'Step 3 of 3: how is your box'
    };
    var steps = el('div', { class: 'hc-rail__steps', 'aria-hidden': 'true' });
    for (var i = 0; i < labels.length; i++) {
      var cls = 'hc-rail__step';
      if (i + 1 < step) cls += ' hc-rail__step--done';
      else if (i + 1 === step) cls += ' hc-rail__step--now';
      var s = el('span', { class: cls }, [el('span', { class: 'hc-rail__dot' })]);
      s.appendChild(document.createTextNode(labels[i]));
      steps.appendChild(s);
      if (i < labels.length - 1) steps.appendChild(el('span', { class: 'hc-rail__link' }));
    }
    var children = [
      el('span', { class: 'hc-sr-only', text: ariaByStep[step] }),
      steps
    ];
    if (showAnchor && state.order) {
      children.push(orderAnchor());
    }
    return el('div', { class: 'hc-rail', role: 'group', 'aria-label': ariaByStep[step] }, children);
  }

  function orderAnchor() {
    var g = grassFor(state.lineItem);
    var anchor = el('span', { class: 'hc-rail__anchor' });
    anchor.appendChild(ICON.blade());
    anchor.appendChild(el('b', { class: 'hc-rail__anchor__box', text: state.lineItem ? (state.lineItem.title || g.label) : g.label }));
    anchor.appendChild(el('span', { class: 'hc-rail__anchor__dot', 'aria-hidden': 'true' }));
    anchor.appendChild(el('span', { class: 'hc-rail__anchor__order', text: orderName() }));
    return anchor;
  }

  // A reusable screen scaffold. By default it leads with the full-bleed product
  // photo (brandbar), which suits the opening (picker) and the confirmation. Pass
  // hero:'card' on the working screens (path picker + the three forms) to swap the
  // photo for a compact product context card and start the screen at the top, so
  // the question and answers are not pushed down the page.
  function screenScaffold(opts) {
    var asCard = opts.hero === 'card';
    var inner = [];
    if (opts.rail) inner.push(opts.rail);
    if (opts.back) inner.push(opts.back);
    if (asCard) {
      var ctx = productContextCard();
      if (ctx) inner.push(ctx);
    }
    if (opts.eyebrow) {
      inner.push(el('p', { class: 'hc-eyebrow' + (opts.eyebrowMod ? ' hc-eyebrow--' + opts.eyebrowMod : ''), text: opts.eyebrow }));
    }
    if (opts.heading) {
      inner.push(el('h2', { class: 'hc-heading' + (opts.headingMod ? ' ' + opts.headingMod : '') }, htmlBits(opts.heading)));
    }
    if (opts.sub) {
      inner.push(el('p', { class: 'hc-sub' }, htmlBits(opts.sub)));
    }
    appendChildren({ appendChild: function (c) { inner.push(c); } }, opts.body || []);

    var screen = el('div', { class: 'hc-screen' + (asCard ? ' hc-screen--flat' : '') }, inner);
    return el('div', { class: 'hc-wizard' }, asCard ? [screen] : [brandbar(), screen]);
  }

  // A "done"/confirmation screen scaffold: brandbar + rail + the hero block.
  function doneScaffold(opts) {
    var hero = el('div', { class: 'hc-done__hero', role: 'status', 'aria-live': 'polite' }, [
      el('p', { class: 'hc-eyebrow' + (opts.eyebrowMod ? ' hc-eyebrow--' + opts.eyebrowMod : ''), text: opts.eyebrow }),
      el('h2', { class: 'hc-done__title' }, htmlBits(opts.title)),
      opts.lead
        ? el('p', { class: 'hc-done__lead' }, htmlBits(opts.lead))
        : el('p', { class: 'hc-done__text' }, htmlBits(opts.text))
    ]);

    var done = el('div', { class: 'hc-done' }, [hero]);
    appendChildren(done, opts.body || []);

    var inner = [];
    if (opts.rail) inner.push(opts.rail);
    inner.push(done);
    var screen = el('div', { class: 'hc-screen' }, inner);
    return el('div', { class: 'hc-wizard' }, [brandbar(), screen]);
  }

  function seal(variant, motion, iconNode) {
    var s = el('span', { class: 'hc-done__seal hc-done__seal--' + variant + ' hc-done__seal--' + motion });
    s.appendChild(iconNode);
    return s;
  }

  // Parse a tiny subset of inline markup (<strong>, <b>) in copy so the design's
  // emphasis ports cleanly without an innerHTML round-trip on untrusted data.
  function htmlBits(str) {
    if (typeof str !== 'string') return str;
    var out = [];
    var re = /<(strong|b)>(.*?)<\/\1>/g;
    var last = 0, m;
    while ((m = re.exec(str)) !== null) {
      if (m.index > last) out.push(str.slice(last, m.index));
      out.push(el(m[1], { text: m[2] }));
      last = re.lastIndex;
    }
    if (last < str.length) out.push(str.slice(last));
    return out.length ? out : str;
  }

  // -------------------------------------------------------------------------
  // Reusable controls
  // -------------------------------------------------------------------------

  function button(label, onClick, variant, iconNode) {
    var b = el('button', {
      type: 'button',
      class: 'hc-btn' + (variant ? ' hc-btn--' + variant : ''),
      onClick: onClick
    });
    if (iconNode) b.appendChild(iconNode);
    b.appendChild(document.createTextNode(label));
    return b;
  }

  function linkButton(label, href, variant, iconNode) {
    var a = el('a', {
      class: 'hc-btn' + (variant ? ' hc-btn--' + variant : ''),
      href: href || '#',
      target: '_blank',
      rel: 'noopener'
    });
    if (iconNode) a.appendChild(iconNode);
    a.appendChild(document.createTextNode(label));
    return a;
  }

  // The Hub owns its support navigation. When WhatsApp is not configured, the
  // embedded wizard emits one local intent that care.js turns into a real
  // Account and help transition. No dead link and no invented contact channel.
  function accountHelpButton(label) {
    return button(label || 'Open Account and help', function () {
      var event;
      try {
        event = new CustomEvent('wh:happy-cats-account-help', { bubbles: true });
      } catch (e) {
        event = document.createEvent('CustomEvent');
        event.initCustomEvent('wh:happy-cats-account-help', true, false, null);
      }
      root.dispatchEvent(event);
    }, 'ghost');
  }

  function actions(items, pivot) {
    var kids = (items || []).filter(function (i) { return !!i; });
    if (!kids.length) return null;
    return el('div', { class: 'hc-actions' + (pivot ? ' hc-actions--pivot' : '') }, kids);
  }

  function errorBanner(message) {
    var span = el('span', { class: 'hc-error__icon' });
    span.appendChild(ICON.info());
    return el('div', { class: 'hc-error', role: 'alert' }, [
      span,
      el('span', {}, htmlBits(message))
    ]);
  }

  function backLink(label, onClick) {
    var b = el('button', { type: 'button', class: 'hc-back', onClick: onClick });
    b.appendChild(ICON.chevronL());
    b.appendChild(document.createTextNode(label));
    return b;
  }

  function restartLink() {
    return el('button', {
      type: 'button',
      class: 'hc-restart',
      text: 'Look up a different order',
      onClick: resetWizard
    });
  }

  function checkAnotherLink() {
    return el('button', {
      type: 'button',
      class: 'hc-restart',
      text: 'Check another box',
      onClick: resetWizard
    });
  }

  function doneNote(text) {
    return el('p', { class: 'hc-done__note' }, htmlBits(text));
  }

  function setBusy(btn, busy, busyLabel, idleLabel) {
    if (!btn) return;
    btn.disabled = busy;
    clear(btn);
    if (busy) {
      btn.classList.add('is-disabled');
      btn.setAttribute('aria-busy', 'true');
      btn.appendChild(el('span', { class: 'hc-btn__spinner' }));
      btn.appendChild(document.createTextNode(busyLabel));
    } else {
      btn.classList.remove('is-disabled');
      btn.removeAttribute('aria-busy');
      btn.appendChild(document.createTextNode(idleLabel));
    }
  }

  // Multi-select chips (review "what they loved"). Tracks selection on a Set.
  function loveChips(options, selectedSet) {
    var wrap = el('div', { class: 'hc-chips', role: 'group', 'aria-label': 'What they loved' });
    options.forEach(function (label) {
      var tick = el('span', { class: 'hc-chip__tick' });
      tick.appendChild(ICON.check());
      var chip = el('button', {
        type: 'button',
        class: 'hc-chip',
        'aria-pressed': 'false'
      }, [tick]);
      chip.appendChild(document.createTextNode(label));
      chip.addEventListener('click', function () {
        var on = selectedSet.has(label);
        if (on) selectedSet.delete(label);
        else selectedSet.add(label);
        chip.classList.toggle('is-on', !on);
        chip.setAttribute('aria-pressed', !on ? 'true' : 'false');
      });
      wrap.appendChild(chip);
    });
    return wrap;
  }

  // Single-select reason radio chips, grouped (report). Tracks the chosen id on
  // a holder object and supports roving-tabindex arrow keys.
  function reasonChipset(groups, holder, onChange) {
    var allChips = [];
    var setEl = el('div', { class: 'hc-chipset', role: 'radiogroup', 'aria-label': 'What looks off' });

    function selectChip(chip) {
      holder.id = chip.getAttribute('data-id');
      allChips.forEach(function (c) {
        var on = c === chip;
        c.classList.toggle('is-on', on);
        c.setAttribute('aria-checked', on ? 'true' : 'false');
        c.setAttribute('tabindex', on ? '0' : '-1');
      });
      if (onChange) onChange(holder.id);
    }

    groups.forEach(function (group) {
      var chips = el('div', { class: 'hc-chips' });
      group.options.forEach(function (opt) {
        var dot = el('span', { class: 'hc-chip__dot', 'aria-hidden': 'true' });
        var chip = el('button', {
          type: 'button',
          class: 'hc-chip hc-chip--radio',
          role: 'radio',
          'aria-checked': 'false',
          tabindex: '-1',
          'data-id': opt.id
        }, [dot]);
        chip.appendChild(document.createTextNode(opt.label));
        chip.addEventListener('click', function () { selectChip(chip); });
        chips.appendChild(chip);
        allChips.push(chip);
      });
      setEl.appendChild(el('div', { class: 'hc-chipset__group' }, [
        el('p', { class: 'hc-chipset__label', text: group.label }),
        chips
      ]));
    });

    // First chip is the keyboard entry point until one is chosen.
    if (allChips.length) allChips[0].setAttribute('tabindex', '0');

    setEl.addEventListener('keydown', function (e) {
      var cur = document.activeElement;
      var i = allChips.indexOf(cur);
      if (i === -1) return;
      var next = null;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = allChips[(i + 1) % allChips.length];
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = allChips[(i - 1 + allChips.length) % allChips.length];
      else if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); selectChip(cur); return; }
      if (next) { e.preventDefault(); selectChip(next); next.focus(); }
    });

    return setEl;
  }

  // -------------------------------------------------------------------------
  // Photo uploader (faithful to the reference: tap/drag, optimistic preview,
  // saved badge, warm-clay rejection that stays tappable). Stores File on
  // store.file. `prompt`/`formats`/`required` drive copy and behavior.
  // -------------------------------------------------------------------------

  function photoField(store, opts) {
    opts = opts || {};
    var label = el('label', { class: 'hc-photo' });

    function makeInput() {
      var input = el('input', {
        type: 'file',
        accept: 'image/*,image/heic,image/heif',
        class: 'hc-sr-only'
      });
      if (opts.required) { input.setAttribute('required', ''); input.setAttribute('aria-required', 'true'); }
      input.addEventListener('change', function () {
        if (input.files && input.files[0]) accept(input.files[0]);
      });
      return input;
    }

    function renderEmpty() {
      store.file = null;
      label.className = 'hc-photo';
      clear(label);
      var input = makeInput();
      label.appendChild(input);

      var content = el('div', { class: 'hc-photo__content' });
      var ic = el('span', { class: 'hc-photo__icon' });
      ic.appendChild(ICON.camera());
      content.appendChild(ic);

      var lbl = el('span', { class: 'hc-photo__label' });
      lbl.appendChild(el('span', { class: 'hc-photo__pick', text: 'Tap to add' }));
      lbl.appendChild(el('span', { class: 'hc-photo__tail' }));
      lbl.appendChild(el('span', { class: 'hc-photo__drag', text: ' or drag a photo in' }));
      lbl.appendChild(document.createTextNode(' ' + (opts.prompt || 'of your cat enjoying it')));
      content.appendChild(lbl);

      content.appendChild(el('span', { class: 'hc-photo__formats', text: 'JPG, PNG, or HEIC, resized for you' }));
      label.appendChild(content);
    }

    function renderRejected(reason) {
      store.file = null;
      label.className = 'hc-photo hc-photo--rejected';
      clear(label);
      label.appendChild(makeInput());

      var content = el('div', { class: 'hc-photo__content' });
      var ic = el('span', { class: 'hc-photo__icon' });
      ic.appendChild(ICON.camera());
      content.appendChild(ic);

      var p = el('p', { class: 'hc-photo__reason' });
      p.appendChild(el('b', { text: reason.lead }));
      p.appendChild(document.createTextNode(' ' + reason.body + ' '));
      p.appendChild(el('span', { class: 'hc-photo__pick hc-photo__retry', text: 'Tap to try another' }));
      p.appendChild(document.createTextNode('.'));
      content.appendChild(p);

      content.appendChild(el('span', { class: 'hc-photo__formats', text: 'JPG, PNG, or HEIC, resized for you' }));
      label.appendChild(content);
      announce(reason.lead + ' ' + reason.body + ' Tap to try another.');
    }

    function renderFilled(file, src) {
      store.file = file;
      label.className = 'hc-photo hc-photo--filled hc-photo--uploading';
      clear(label);
      var input = makeInput();
      label.appendChild(input);

      var thumb = el('span', { class: 'hc-photo__thumb', role: 'img', 'aria-label': 'Selected photo preview' });
      if (src) {
        thumb.appendChild(el('img', { src: src, alt: '' }));
      } else {
        thumb.appendChild(ICON.photo());
      }
      var progress = el('span', { class: 'hc-photo__progress' });
      thumb.appendChild(progress);

      var fileBox = el('span', { class: 'hc-photo__file' }, [
        el('span', { class: 'hc-photo__name', text: file.name || 'photo.jpg' }),
        el('span', { class: 'hc-photo__size', text: bytes(file.size) })
      ]);
      var status = el('span', { class: 'hc-photo__status', 'aria-live': 'polite', text: 'Adding your photo' });
      fileBox.appendChild(status);

      var remove = el('button', {
        type: 'button',
        class: 'hc-photo__remove',
        text: 'Remove',
        'aria-label': 'Remove photo ' + (file.name || 'photo.jpg')
      });
      remove.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        renderEmpty();
      });

      label.appendChild(el('span', { class: 'hc-photo__preview' }, [thumb, fileBox, remove]));

      // Optimistic settle: progress sweeps, then a saved badge + status land.
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      function settle() {
        if (progress.parentNode) progress.parentNode.removeChild(progress);
        var saved = el('span', { class: 'hc-photo__saved', 'aria-hidden': 'true' });
        saved.appendChild(ICON.check());
        thumb.appendChild(saved);
        clear(status);
        status.appendChild(ICON.check());
        // "Added", not "Saved" (Fable studio 2026-07-05 + Opus 2026-07-06):
        // nothing has uploaded yet — the real upload happens at submit — and
        // "Ready to send" collided with the "Send to our team" button, reading
        // as "already sent?". "Added" states the file's condition only.
        status.appendChild(document.createTextNode('Added'));
        label.classList.remove('hc-photo--uploading');
      }
      if (reduce) settle(); else setTimeout(settle, 900);
    }

    function accept(file) {
      if (!file) return;
      if (file.size > MAX_PHOTO_BYTES) {
        renderRejected({ lead: 'That photo is very large.', body: 'A quick re-snap or a smaller one works. We resize it for you before sending.' });
        return;
      }
      if (!acceptablePhotoType(file)) {
        renderRejected({ lead: 'That file is not a photo we can read.', body: 'A JPG, PNG, or HEIC photo works.' });
        return;
      }
      var src = (window.URL && file.type && file.type.indexOf('image/') === 0) ? URL.createObjectURL(file) : null;
      renderFilled(file, src);
    }

    // Drag/drop (fine pointer only via CSS; logic is harmless on touch).
    ['dragenter', 'dragover'].forEach(function (ev) {
      label.addEventListener(ev, function (e) {
        e.preventDefault();
        if (!label.classList.contains('hc-photo--filled')) label.classList.add('hc-photo--dragover');
      });
    });
    ['dragleave', 'dragend'].forEach(function (ev) {
      label.addEventListener(ev, function (e) { if (e.target === label) label.classList.remove('hc-photo--dragover'); });
    });
    label.addEventListener('drop', function (e) {
      e.preventDefault();
      label.classList.remove('hc-photo--dragover');
      var dt = e.dataTransfer;
      if (dt && dt.files && dt.files[0]) accept(dt.files[0]);
    });

    // Submit-time decode failures flip the FIELD to its rejected state too, so
    // a stale "Ready to send" never sits above a cannot-read-photo banner
    // (Opus challenge 2026-07-06).
    store.reject = function () {
      renderRejected({ lead: 'We could not read that photo.', body: 'A JPG or PNG works best here.' });
    };

    renderEmpty();
    return label;
  }

  // A shared polite region for photo rejections (the visible reason is in-control).
  var photoAnnouncer = (function () {
    var elm = document.createElement('div');
    elm.className = 'hc-sr-only';
    elm.setAttribute('aria-live', 'polite');
    elm.setAttribute('aria-atomic', 'true');
    document.body.appendChild(elm);
    return elm;
  })();
  function announce(text) {
    photoAnnouncer.textContent = '';
    photoAnnouncer.textContent = text;
  }

  // Free-text counter: invisible until the field nears its cap, warming clay.
  function wireCounter(field, counter) {
    var max = parseInt(field.getAttribute('maxlength'), 10) || 0;
    if (!max) return;
    var NEAR = Math.max(0, max - 100);
    var lastBand = '';
    function update() {
      var len = field.value.length;
      counter.textContent = len + ' / ' + max;
      var band = len >= max ? 'limit' : (len >= NEAR ? 'near' : 'rest');
      counter.classList.toggle('hc-counter--near', band === 'near');
      counter.classList.toggle('hc-counter--limit', band === 'limit');
      if (band !== lastBand) {
        if (band === 'near') counter.setAttribute('aria-label', 'Approaching the limit, ' + (max - len) + ' characters left');
        else if (band === 'limit') counter.setAttribute('aria-label', 'Character limit reached');
        else counter.removeAttribute('aria-label');
        lastBand = band;
      }
    }
    field.addEventListener('input', update);
    field.addEventListener('blur', function () {
      var norm = (field.value || '').replace(/\s+/g, ' ').trim();
      if (norm !== field.value) { field.value = norm; update(); }
    });
    update();
  }

  // -------------------------------------------------------------------------
  // Step 1: Find your order
  // -------------------------------------------------------------------------

  function renderLookup(prefill) {
    setPath(null);
    var errorSlot = el('div', { class: 'hc-error-slot' });

    var orderInput = el('input', {
      type: 'text',
      class: 'hc-input',
      id: 'hc-order',
      name: 'order_number',
      inputmode: 'text',
      autocomplete: 'off',
      placeholder: '#1042',
      'aria-describedby': 'hc-order-hint',
      value: (prefill && prefill.order) || ''
    });
    var orderHint = el('p', { class: 'hc-hint', id: 'hc-order-hint', text: 'On your box label and your confirmation email.' });

    // The server matches order_number + contact, where contact must equal the
    // order's email or one of its phones. Both are required and named honestly:
    // a single-field shortcut cannot work against the backend, so we do not imply
    // one.
    var contactInput = el('input', {
      type: 'text',
      class: 'hc-input',
      id: 'hc-contact',
      name: 'contact',
      inputmode: 'email',
      autocomplete: 'email',
      placeholder: 'you@example.com or 05x xxx xxxx',
      'aria-describedby': 'hc-contact-hint',
      value: (prefill && prefill.contact) || ''
    });
    var contactHint = el('p', { class: 'hc-hint', id: 'hc-contact-hint', text: 'We check both so only the person who placed the order can open it.' });

    // Honeypot: hidden from real customers; a filled value flags a bot. Never
    // populated. The server drops it silently.
    var honeypot = el('input', {
      type: 'text',
      name: 'website',
      class: 'hc-hp',
      tabindex: '-1',
      autocomplete: 'off',
      'aria-hidden': 'true'
    });

    var submit = button('Find my box', null, null);
    submit.setAttribute('type', 'submit');

    var noteLine = prefill && prefill.note
      ? el('p', { class: 'hc-hint hc-note', text: prefill.note })
      : null;

    var form = el('form', { class: 'hc-form', novalidate: true }, [
      noteLine,
      el('div', { class: 'hc-field' }, [
        el('label', { class: 'hc-label', for: 'hc-order', text: 'Order number' }),
        orderInput,
        orderHint
      ]),
      el('div', { class: 'hc-field' }, [
        el('label', { class: 'hc-label', for: 'hc-contact', text: 'Email or UAE phone on the order' }),
        contactInput,
        contactHint
      ]),
      honeypot,
      errorSlot,
      submit
    ]);

    function clearLookupError() {
      clear(errorSlot);
      orderInput.removeAttribute('aria-invalid');
      contactInput.removeAttribute('aria-invalid');
    }
    orderInput.addEventListener('input', clearLookupError);
    contactInput.addEventListener('input', clearLookupError);

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      clear(errorSlot);

      var orderNumber = orderInput.value.trim();
      var contact = contactInput.value.trim();

      if (!orderNumber || !contact) {
        errorSlot.appendChild(errorBanner('Add your order number and the email or phone from the order.'));
        return;
      }
      var orderField = orderNumber;
      var contactField = contact;

      state.submitting = true;
      setBusy(submit, true, 'Finding your box', 'Find my box');
      orderInput.disabled = true;
      contactInput.disabled = true;

      postLookup(orderField, contactField)
        .then(function (resp) {
          state.submitting = false;
          setBusy(submit, false, 'Finding your box', 'Find my box');
          orderInput.disabled = false;
          contactInput.disabled = false;

          if (resp.ok && resp.body && resp.body.token && resp.body.order) {
            state.token = resp.body.token;
            state.order = resp.body.order;
            advanceFromLookup();
            return;
          }
          // Honeypot benign success (no order): treat as a soft generic miss.
          if (resp.ok && resp.body && resp.body.ok && !resp.body.order) {
            renderLookupError(orderField, contactField, GENERIC_ERROR);
            return;
          }
          if (resp.status === 429) {
            errorSlot.appendChild(errorBanner('Too many tries for now. Please wait a little while and try again.'));
            return;
          }
          // 400, 404, and anything else collapse to one safe message.
          renderLookupError(orderField, contactField, (resp.body && resp.body.error) || GENERIC_ERROR);
        })
        .catch(function () {
          state.submitting = false;
          setBusy(submit, false, 'Finding your box', 'Find my box');
          orderInput.disabled = false;
          contactInput.disabled = false;
          errorSlot.appendChild(errorBanner(NETWORK_ERROR));
        });
    });

    setScreen(
      screenScaffold({
        rail: rail(1, false),
        eyebrow: 'Welcome back',
        heading: 'Let us find your box',
        sub: 'Enter your order number and the email or phone on the order. Our team reads every one.',
        body: [form, el('p', { class: 'hc-proof' }, [el('b', { text: '8,000+ cats served' }), ' across the UAE'])]
      })
    );

    setTimeout(function () {
      if (orderInput && typeof orderInput.focus === 'function') orderInput.focus();
    }, 60);
  }

  // The "not found" lookup screen: one warm, generic message + a WhatsApp fallback.
  function renderLookupError(orderVal, contactVal, message) {
    setPath(null);
    var errorSlot = el('div', { class: 'hc-error-slot' });

    var orderInput = el('input', {
      type: 'text',
      class: 'hc-input',
      id: 'hc-order',
      name: 'order_number',
      inputmode: 'text',
      autocomplete: 'off',
      placeholder: '#1042',
      'aria-invalid': 'true',
      'aria-describedby': 'hc-order-hint',
      value: orderVal || ''
    });
    var contactInput = el('input', {
      type: 'text',
      class: 'hc-input',
      id: 'hc-contact',
      name: 'contact',
      inputmode: 'email',
      autocomplete: 'email',
      placeholder: 'you@example.com or 05x xxx xxxx',
      'aria-describedby': 'hc-contact-hint',
      value: (contactVal && contactVal !== orderVal) ? contactVal : ''
    });
    var honeypot = el('input', { type: 'text', name: 'website', class: 'hc-hp', tabindex: '-1', autocomplete: 'off', 'aria-hidden': 'true' });

    var submit = button('Try again', null, null);
    submit.setAttribute('type', 'submit');

    var helpBtn = CONFIG.whatsappNumber
      ? linkButton('Message us to find it', whatsappLink('Hi Sprouties, I am trying to find my order for the Happy Cats Guarantee.'), 'ghost', ICON.whatsapp())
      : accountHelpButton();

    var initialError = errorBanner(message);
    var form = el('form', { class: 'hc-form', novalidate: true }, [
      el('div', { class: 'hc-field hc-field--invalid' }, [
        el('label', { class: 'hc-label', for: 'hc-order', text: 'Order number' }),
        orderInput,
        el('p', { class: 'hc-hint', id: 'hc-order-hint', text: 'On your box label and your confirmation email.' })
      ]),
      el('div', { class: 'hc-field' }, [
        el('label', { class: 'hc-label', for: 'hc-contact', text: 'Email or UAE phone on the order' }),
        contactInput,
        el('p', { class: 'hc-hint', id: 'hc-contact-hint', text: 'We check both so only the person who placed the order can open it.' })
      ]),
      honeypot,
      errorSlot,
      submit,
      helpBtn
    ]);

    function clearLookupError() {
      clear(errorSlot);
      if (initialError.parentNode) initialError.parentNode.removeChild(initialError);
      orderInput.removeAttribute('aria-invalid');
      contactInput.removeAttribute('aria-invalid');
    }
    orderInput.addEventListener('input', clearLookupError);
    contactInput.addEventListener('input', clearLookupError);

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      clear(errorSlot);
      var o = orderInput.value.trim();
      var c = contactInput.value.trim();
      if (!o || !c) {
        errorSlot.appendChild(errorBanner('Add your order number and the email or phone from the order.'));
        return;
      }
      var orderField = o, contactField = c;
      state.submitting = true;
      setBusy(submit, true, 'Finding your box', 'Try again');
      orderInput.disabled = true; contactInput.disabled = true;
      postLookup(orderField, contactField)
        .then(function (resp) {
          state.submitting = false;
          setBusy(submit, false, 'Finding your box', 'Try again');
          orderInput.disabled = false; contactInput.disabled = false;
          if (resp.ok && resp.body && resp.body.token && resp.body.order) {
            state.token = resp.body.token;
            state.order = resp.body.order;
            advanceFromLookup();
            return;
          }
          if (resp.status === 429) {
            errorSlot.appendChild(errorBanner('Too many tries for now. Please wait a little while and try again.'));
            return;
          }
          errorSlot.appendChild(errorBanner((resp.body && resp.body.error) || GENERIC_ERROR));
        })
        .catch(function () {
          state.submitting = false;
          setBusy(submit, false, 'Finding your box', 'Try again');
          orderInput.disabled = false; contactInput.disabled = false;
          errorSlot.appendChild(errorBanner(NETWORK_ERROR));
        });
    });

    setScreen(
      screenScaffold({
        rail: rail(1, false),
        eyebrow: 'Welcome back',
        heading: 'Let us find your box',
        sub: 'Enter your order number and the email or phone on the order. Our team reads every one.',
        body: [initialError, form]
      })
    );
  }

  // SESSION MODE: the embedding page vouched for the signed-in customer with
  // their latest order + account email. Run the same server-verified lookup
  // without asking them to type anything; the success path lands on the normal
  // "Found it" screen (one Continue tap + the different-order escape), and any
  // miss falls back to the manual form, prefilled and calmly explained.
  function renderAutoLookup() {
    setPath(null);
    setScreen(
      screenScaffold({
        rail: rail(1, false),
        eyebrow: 'Welcome back',
        heading: 'Finding your latest box',
        sub: 'One moment. You are signed in, so no forms needed.',
        body: [el('p', { class: 'hc-proof' }, [el('b', { text: '8,000+ cats served' }), ' across the UAE'])]
      })
    );
    postLookup(CONFIG.autoOrder, CONFIG.autoContact)
      .then(function (resp) {
        if (resp.ok && resp.body && resp.body.token && resp.body.order) {
          state.token = resp.body.token;
          state.order = resp.body.order;
          state.autoEntered = true;
          advanceFromLookup();
          return;
        }
        var note =
          resp.status === 429
            ? 'Too many tries for now. Please wait a little while and try again.'
            : 'We could not pull that box up automatically. Check the details below and we will find it.';
        renderLookup({ order: CONFIG.autoOrder, contact: CONFIG.autoContact, note: note });
      })
      .catch(function () {
        renderLookup({
          order: CONFIG.autoOrder,
          contact: CONFIG.autoContact,
          note: 'We could not reach our team just now. Check your connection and try below in a moment.'
        });
      });
  }

  // After a successful lookup, skip the product picker when the order has a
  // single line item, otherwise show it.
  function advanceFromLookup() {
    var items = (state.order && state.order.line_items) || [];
    if (items.length <= 1) {
      state.lineItem = items[0] || null;
      // Session mode: the customer asked for help while signed in; do not add
      // a confirm tap here. The path screen shows the found box plainly and
      // keeps "Not this box?" one line away.
      if (state.autoEntered) {
        renderPathPicker();
      } else {
        renderSinglePicker();
      }
    } else {
      renderProductPicker();
    }
  }

  // -------------------------------------------------------------------------
  // Step 2: Pick the product
  // -------------------------------------------------------------------------

  // Single box: confirm and move straight on. The real box photo leads (the
  // hero); the card just names the box and the order, plainly.
  function renderSinglePicker() {
    setPath(null);
    var li = state.lineItem;
    var boxName = li ? (cleanTitle(li.title) || grassFor(li).label) : grassFor(li).label;

    var bits = [];
    if (state.order && state.order.is_subscriber) bits.push('Subscriber');
    if (orderName()) bits.push(orderName());
    if (placedLine()) bits.push(placedLine());

    setScreen(
      screenScaffold({
        eyebrow: 'Your order',
        heading: greetingHeading('Found it'),
        body: [
          productLine(boxName, bits.join('  ·  ')),
          actions([button('Continue', renderPathPicker, null)]),
          restartLink()
        ]
      })
    );
  }

  // A plain product line: the box name and a quiet meta row. No pill, no icons.
  function productLine(name, meta) {
    var kids = [el('p', { class: 'hc-productline__name', text: name })];
    if (meta) kids.push(el('p', { class: 'hc-productline__meta', text: meta }));
    return el('div', { class: 'hc-productline' }, kids);
  }

  function renderProductPicker() {
    setPath(null);
    var items = state.order.line_items || [];

    var list = el('div', { class: 'hc-products' });
    items.forEach(function (li) {
      var g = grassFor(li);
      list.appendChild(
        el('button', {
          type: 'button',
          class: 'hc-product hc-product--tap',
          onClick: function () { state.lineItem = li; renderPathPicker(); }
        }, [
          productThumb(li),
          el('span', { class: 'hc-product__body' }, [
            el('span', { class: 'hc-product__title', text: cleanTitle(li.title) || g.label }),
            el('span', { class: 'hc-product__meta', text: g.meta })
          ])
        ])
      );
    });

    setScreen(
      screenScaffold({
        eyebrow: 'Your order',
        heading: greetingHeading('Which box is this about', '?'),
        sub: 'Tap the box this is about.',
        body: [list, restartLink()]
      })
    );
  }

  // Trim the storefront product title to its clean core for display: drop the
  // " | Sprouties UAE" suffix and any " - Fresh Live Box" descriptor, so no stray
  // dashes or pipes reach the customer.
  function cleanTitle(t) {
    if (!t) return t;
    return String(t).split('|')[0].split(' - ')[0].replace(/\s+/g, ' ').trim();
  }

  // A product thumbnail from the real storefront image, with a soft placeholder.
  // The placeholder carries the grass-blade glyph (Opus challenge 2026-07-06:
  // an empty cream square next to the found-box title read as broken, and any
  // live product without a featured image would hit it too).
  function productThumb(li) {
    if (li && li.image) {
      return el('img', { class: 'hc-product__img', src: li.image, alt: '', loading: 'lazy' });
    }
    var ph = el('span', { class: 'hc-product__img hc-product__img--ph' });
    ph.appendChild(ICON.blade());
    return ph;
  }

  // A compact, non-interactive product card for the working screens: the real box
  // photo, its clean name, and the order it belongs to. The same card shape the
  // customer already saw in the picker, used here as quiet context so the box
  // leads without a full photo hero pushing the task down the page.
  function productContextCard() {
    var li = state.lineItem;
    if (!li) return null;
    var name = cleanTitle(li.title) || grassFor(li).label;
    var bits = [];
    if (orderName()) bits.push(orderName());
    if (placedLine()) bits.push(placedLine());
    return el('div', { class: 'hc-context' }, [
      productThumb(li),
      el('div', { class: 'hc-context__body' }, [
        el('p', { class: 'hc-context__title', text: name }),
        bits.length ? el('p', { class: 'hc-context__meta', text: bits.join(' · ') }) : null
      ])
    ]);
  }

  function optionGo() {
    var go = el('span', { class: 'hc-option__go' });
    go.appendChild(ICON.chevron());
    return go;
  }

  // -------------------------------------------------------------------------
  // Step 3: Pick a path (the three doors)
  // -------------------------------------------------------------------------

  function renderPathPicker() {
    setPath(null);
    var g = grassFor(state.lineItem);

    var doors = [
      { path: 'review',        title: 'My cat is happy',   meta: 'Leave a quick review',                door: 'review',  iconCls: 'oat',    iconNode: ICON.star,   go: renderReviewForm },
      { path: 'problem_claim', title: 'Something looks off', meta: 'Tell us and we will make it right',  door: 'problem', iconCls: 'barley', iconNode: ICON.shield, go: renderProblemForm },
      { path: 'not_eating',    title: 'Not nibbling yet',   meta: 'Get a simple starter guide',          door: 'eating',  iconCls: 'wheat',  iconNode: ICON.sprout, go: renderNotEatingForm }
    ];

    var list = el('div', { class: 'hc-paths' });
    doors.forEach(function (d) {
      var go = el('span', { class: 'hc-pathcard__go' });
      go.appendChild(ICON.chevron());
      list.appendChild(
        el('button', {
          type: 'button',
          class: 'hc-pathcard hc-pathcard--' + d.door,
          'aria-label': d.title,
          onClick: function () { state.path = d.path; d.go(); }
        }, [
          el('span', { class: 'hc-pathcard__body' }, [
            el('span', { class: 'hc-pathcard__title', text: d.title }),
            el('span', { class: 'hc-pathcard__meta', text: d.meta })
          ]),
          go
        ])
      );
    });

    var back = (state.order.line_items || []).length > 1
      ? backLink('Pick a different box', renderProductPicker)
      : null;

    // Session mode (single box): the scaffold's hero card already names the
    // found box and order plainly; the escape under the choices is relabeled
    // so "wrong box" is one obvious line away (Codex-reviewed guardrail).
    var escape = restartLink();
    if (state.autoEntered && (state.order.line_items || []).length <= 1) {
      escape = el('button', {
        type: 'button',
        class: 'hc-restart',
        text: 'Not this box? Look up another order',
        onClick: resetWizard
      });
    }

    setScreen(
      screenScaffold({
        hero: 'card',
        rail: rail(3, true),
        back: back,
        eyebrow: 'Your box',
        heading: greetingHeading('How is it going', '?'),
        sub: 'Pick the one that fits. We will take it from here.',
        body: [list, escape]
      })
    );
  }

  // -------------------------------------------------------------------------
  // Step 4a: Review form
  // -------------------------------------------------------------------------

  function renderReviewForm() {
    setPath('review');
    var rating = { value: 0 };
    var lovedSet = new Set();
    var photoStore = { file: null };
    var errorSlot = el('div', { class: 'hc-error-slot' });

    // Star picker (radiogroup, roving tabindex, reaction word reveal + recovery).
    var capId = 'hc-rev-cap';
    var stars = el('div', { class: 'hc-stars', role: 'radiogroup', 'aria-labelledby': capId });
    var starButtons = [];
    for (var n = 1; n <= 5; n++) {
      (function (value) {
        var b = el('button', {
          type: 'button',
          class: 'hc-star',
          role: 'radio',
          'aria-checked': 'false',
          tabindex: value === 1 ? '0' : '-1',
          'aria-label': value + ' star' + (value > 1 ? 's' : '') + (value === 5 ? ', excellent' : '')
        });
        b.appendChild(ICON.star());
        b.addEventListener('click', function () { setRating(value); b.focus(); });
        starButtons.push(b);
        stars.appendChild(b);
      })(n);
    }

    var word = el('p', { class: 'hc-rate__word' });
    var reveal = el('div', { class: 'hc-rate__reveal hc-rate__reveal--closed', 'aria-hidden': 'true' }, [word]);

    var recoverText = el('p', { class: 'hc-rate__recover-text', text: 'If something looked off, we would like to make it right. A photo helps our team look into it.' });
    var recoverBtn = button('Tell us what happened', function () {
      state.path = 'problem_claim';
      renderProblemForm();
    }, 'ghost');
    recoverBtn.classList.add('hc-btn--sm');
    var recover = el('div', { class: 'hc-rate__recover hc-rate__recover--closed', role: 'group', 'aria-label': 'Make it right' }, [
      el('div', { class: 'hc-rate__recover-inner' }, [recoverText, recoverBtn])
    ]);

    function setRating(value) {
      rating.value = value;
      clear(errorSlot);
      starButtons.forEach(function (b, i) {
        var on = i < value;
        var checked = i === value - 1;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-checked', checked ? 'true' : 'false');
        b.setAttribute('tabindex', checked ? '0' : '-1');
      });
      word.textContent = STAR_WORDS[value] || '';
      reveal.classList.remove('hc-rate__reveal--closed');
      reveal.removeAttribute('aria-hidden');
      recover.classList.toggle('hc-rate__recover--closed', value > 3);
      submit.classList.remove('is-disabled');
      submit.removeAttribute('disabled');
      submit.removeAttribute('aria-disabled');
    }

    stars.addEventListener('keydown', function (e) {
      var i = starButtons.indexOf(document.activeElement);
      if (i === -1) return;
      var next = null;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % starButtons.length;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + starButtons.length) % starButtons.length;
      else if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setRating(i + 1); return; }
      if (next != null) { e.preventDefault(); setRating(next + 1); starButtons[next].focus(); }
    });

    var chips = loveChips(LOVE_CHIPS, lovedSet);
    var photo = photoField(photoStore, { required: false, prompt: 'of your cat enjoying it' });

    var submit = button('Post my review', null, 'gold');
    submit.setAttribute('type', 'submit');
    submit.classList.add('is-disabled');
    submit.setAttribute('disabled', '');
    submit.setAttribute('aria-disabled', 'true');

    var photoHead = el('p', { class: 'hc-fieldhead', id: 'hc-rev-photo-head' });
    photoHead.appendChild(document.createTextNode('A photo of your cat '));
    photoHead.appendChild(el('span', { class: 'hc-label__opt', text: '(optional)' }));

    var form = el('form', { class: 'hc-form', novalidate: true }, [
      el('div', { class: 'hc-rate' }, [
        el('p', { class: 'hc-rate__caption', id: capId, text: 'Your rating' }),
        stars,
        reveal,
        recover
      ]),
      el('div', { class: 'hc-field' }, [
        el('p', { class: 'hc-fieldhead', text: 'What did your cat love?' }),
        chips
      ]),
      el('div', { class: 'hc-field' }, [photoHead, photo]),
      errorSlot,
      submit,
      restartLink()
    ]);

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      clear(errorSlot);
      if (rating.value < 1) {
        errorSlot.appendChild(errorBanner('Tap a star, 1 to 5, to tell us how the box went. Everything else is optional.'));
        return;
      }

      // Busy state before the encode, same as the problem form (Opus 2026-07-06).
      setBusy(submit, true, 'Sending your review');
      // The review photo is optional, so a photo that cannot be re-encoded is just
      // dropped (preparePhoto resolves null) and the review still posts.
      preparePhoto(photoStore.file, false).then(function (prepared) {
        var fd = baseFormData('review');
        fd.append('rating', String(rating.value));
        var loved = setToArray(lovedSet);
        if (loved.length) fd.append('feedback_text', 'Loved: ' + loved.join(', '));
        if (prepared) fd.append('photo', prepared, prepared.name);

        renderReviewSending();
        submitForm(fd, function () {
          renderReviewConfirmation(rating.value);
        }, function (resp) {
          renderReviewFailed(resp);
        });
      });
    });

    setScreen(
      screenScaffold({
        hero: 'card',
        rail: rail(3, true),
        back: backLink('Back', renderPathPicker),
        eyebrow: 'Your review',
        eyebrowMod: 'oat',
        heading: 'How did your cat like it?',
        sub: boxLabel()
          ? 'Other UAE cat parents read these before they try us, so a few words about the <strong>' + boxLabel() + '</strong> go a long way.'
          : 'Other UAE cat parents read these before they try us, so a few words go a long way.',
        body: [form]
      })
    );
  }

  function renderReviewSending() {
    setScreen(loadingScreen('oat', 'Posting your review', 'Saving your stars and photo. One moment.'));
  }

  function renderReviewConfirmation(ratingValue) {
    setPath('review');
    root.setAttribute('data-hc-journey-complete', '1');
    if (ratingValue >= 4) {
      var actionsList = [];
      if (CONFIG.reviewUrl) {
        actionsList.push(linkButton('Post your public review', CONFIG.reviewUrl, 'gold'));
      }

      var body = [actions(actionsList.length ? actionsList : [])];

      // One quiet referral line, not a second full ask (organic touch: the box
      // line names what they loved as "freshly grown, organic").
      if (CONFIG.referralUrl) {
        var lead = el('p', { class: 'hc-referral__lead', text: 'Know a cat who would love this?' });
        var referral = el('div', { class: 'hc-referral', 'aria-live': 'off' }, [
          lead,
          linkButton('Send a friend a free box', CONFIG.referralUrl, null)
        ]);
        // Restyle the CTA as the quiet underlined referral link, not a full button.
        var refLink = referral.querySelector('a');
        refLink.className = 'hc-textlink hc-referral__link';
        body.push(referral);
      }
      body.push(checkAnotherLink());

      setScreen(
        doneScaffold({
          seal: 'oat',
          sealMotion: 'bloom',
          sealIcon: ICON.star(),
          eyebrowMod: 'oat',
          eyebrow: 'Thank you',
          title: 'So glad your cat is happy',
          text: CONFIG.reviewUrl
            ? 'Your review of your freshly grown, organic box is saved. Use the button below if you would also like to post it publicly.'
            : 'Your review of your freshly grown, organic box is saved for the Sprouties team.',
          body: body
        })
      );
      return;
    }

    // 3 stars and under: the soft signal is logged. Offer recovery, no pressure.
    setScreen(
      doneScaffold({
        seal: 'barley',
        sealMotion: 'rest',
        sealIcon: ICON.heart(),
        eyebrowMod: 'barley',
        eyebrow: 'Thank you',
        title: 'Thank you for the honest rating',
        text: boxLabel()
          ? 'Your rating is saved, and we would like to make the next box better. What happened with the <strong>' + boxLabel() + '</strong>?'
          : 'Your rating is saved, and we would like to make the next box better. What happened?',
        body: [
          actions([
            button('Something looked off, send a photo', function () { state.path = 'problem_claim'; renderProblemForm(); }, null),
            button('My cat has not started nibbling', function () { state.path = 'not_eating'; renderNotEatingForm(); }, 'ghost')
          ]),
          doneNote('Or leave it here. That is enough, and we are glad you told us.'),
          checkAnotherLink()
        ]
      })
    );
  }

  function renderReviewFailed(resp) {
    setPath('review');
    // Expired token: route to a fresh lookup.
    if (resp && resp.status === 401) { renderExpired(); return; }

    setScreen(
      screenScaffold({
        rail: rail(3, true),
        eyebrow: 'Still here',
        eyebrowMod: 'oat',
        heading: 'That did not post just now',
        sub: 'Your stars and photo are saved. It did not post that time, so one more tap usually does it.',
        body: [
          errorBanner('Your review is still here, every star and word, ready to post.'),
          actions([
            retryButton('Try posting again', 'gold', function () {
              renderReviewSending();
              submitForm(resp.fd, function () { renderReviewConfirmation(resp.rating); }, function (r) { renderReviewFailed(r); });
            }),
            button('Leave it for now', renderPathPicker, 'ghost')
          ])
        ]
      })
    );
  }

  // -------------------------------------------------------------------------
  // Step 4b: Problem claim form
  // -------------------------------------------------------------------------

  function reportLabel(id) {
    for (var i = 0; i < REPORT_OPTIONS.length; i++) {
      if (REPORT_OPTIONS[i].id === id) return REPORT_OPTIONS[i].label;
    }
    return id;
  }

  // The one multi-select question: tap a row to toggle it. 'something_else' is
  // exclusive (it clears the others, and any other clears it). `selected` is shared
  // by reference with the caller; onChange fires after every toggle.
  function reportChecklist(options, selected, onChange) {
    var EX = 'something_else';
    var list = el('div', { class: 'hc-checks', role: 'group', 'aria-label': 'What you see' });

    function sync() {
      for (var i = 0; i < list.children.length; i++) {
        var row = list.children[i];
        var on = selected.indexOf(row.getAttribute('data-id')) !== -1;
        row.classList.toggle('is-on', on);
        row.setAttribute('aria-pressed', on ? 'true' : 'false');
      }
    }
    function remove(id) { var i = selected.indexOf(id); if (i !== -1) selected.splice(i, 1); }
    function toggle(id, exclusive) {
      var on = selected.indexOf(id) !== -1;
      if (exclusive) {
        selected.length = 0;
        if (!on) selected.push(id);
      } else {
        remove(EX);
        if (on) remove(id); else selected.push(id);
      }
      sync();
      if (onChange) onChange();
    }

    options.forEach(function (opt) {
      var box = el('span', { class: 'hc-check__box' });
      box.appendChild(ICON.check());
      var row = el('button', {
        type: 'button', class: 'hc-check', 'data-id': opt.id, 'aria-pressed': 'false'
      }, [box, el('span', { class: 'hc-check__label', text: opt.label })]);
      row.addEventListener('click', function () { toggle(opt.id, !!opt.exclusive); });
      list.appendChild(row);
    });
    sync();
    return list;
  }

  function renderProblemForm() {
    // The standalone problem form can create its own side effect. In Hub embed
    // mode that ownership is deliberately intercepted before any form, photo, or
    // /submissions POST exists, so there is one replacement authority only.
    if (handoffProblemToHub()) return;
    setPath('problem_claim');
    var selected = [];
    var photoStore = { file: null };
    var errorSlot = el('div', { class: 'hc-error-slot' });

    function hasOther() { return selected.indexOf('something_else') !== -1; }
    function noteMin() { return hasOther() ? 60 : 12; }

    // Photo is always required, so the field is built once and never rebuilt. That
    // is also what keeps an attached photo from clearing when the customer changes
    // which rows are ticked.
    var photo = photoField(photoStore, { required: true, prompt: 'so our team can see what you see' });
    var photoHead = el('p', { class: 'hc-fieldhead' }, ['Add a photo of the box', el('span', { class: 'hc-req', text: 'Required' })]);
    // The formats line lives once, inside the dropzone (.hc-photo__formats). The
    // hint here says only WHY (Fable studio 2026-07-05: the formats sentence was
    // duplicated verbatim between the dropzone and this hint).
    var photoHint = el('p', { class: 'hc-hint', text: 'It helps our team sort it out fast.' });

    var note = el('textarea', {
      class: 'hc-textarea', id: 'hc-r-msg', name: 'feedback_text', rows: '3', maxlength: '800',
      'aria-describedby': 'hc-r-msg-count', placeholder: 'Tell us what you saw when the box arrived.'
    });
    var counter = el('span', { class: 'hc-counter', id: 'hc-r-msg-count', 'aria-live': 'polite', text: '0 / 800' });
    wireCounter(note, counter);
    var noteHead = el('p', { class: 'hc-fieldhead' }, ['Tell us what happened', el('span', { class: 'hc-req', text: 'Required' })]);
    var noteHint = el('p', { class: 'hc-hint', id: 'hc-r-msg-hint', text: 'A line or two about what you see.' });

    var checks = reportChecklist(REPORT_OPTIONS, selected, function () {
      clear(errorSlot);
      noteHint.textContent = hasOther()
        ? 'Tell us what happened in a bit more detail, at least 60 characters, so our team can help.'
        : 'A line or two about what you see.';
    });

    var submit = button('Send to our team', null, null);
    submit.setAttribute('type', 'submit');

    var form = el('form', { class: 'hc-form', novalidate: true }, [
      // No visible fieldhead here (Fable studio 2026-07-05): the screen sub
      // ("Pick anything that fits, you can choose more than one") already labels
      // this checklist, and the list carries role=group + aria-label for SR. The
      // old 'Pick anything that fits' fieldhead was a verbatim stutter under it.
      el('div', { class: 'hc-field' }, [checks]),
      el('div', { class: 'hc-field' }, [photoHead, photo, photoHint]),
      el('div', { class: 'hc-field' }, [noteHead, note, counter, noteHint]),
      errorSlot,
      submit,
      restartLink()
    ]);
    note.addEventListener('input', function () { clear(errorSlot); });
    var photoInput = photo.querySelector('input[type="file"]');
    if (photoInput) photoInput.addEventListener('change', function () { clear(errorSlot); });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      clear(errorSlot);

      if (!selected.length) {
        errorSlot.appendChild(errorBanner('Pick at least one thing you see so our team can sort it out.'));
        return;
      }
      if (!photoStore.file) {
        errorSlot.appendChild(errorBanner('Add a photo of the box so our team can see it.'));
        return;
      }
      var msg = note.value.trim();
      if (msg.length < noteMin()) {
        errorSlot.appendChild(errorBanner(hasOther()
          ? 'Tell us a bit more about what happened, at least 60 characters.'
          : 'Add a short note about what you see.'));
        return;
      }

      var labels = selected.map(function (id) { return reportLabel(id); });
      // Busy state BEFORE preparePhoto (Opus challenge 2026-07-06): the canvas
      // re-encode of a 12MP phone photo takes 1-3 silent seconds; without this
      // the tap looked dead and invited a double-tap on the make-it-right path.
      setBusy(submit, true, 'Sending to our team');
      preparePhoto(photoStore.file, true).then(function (prepared) {
        var fd = baseFormData('problem_claim');
        // The server takes one enum issue_type, and some values auto-arrange a
        // replacement. We send something_else (manual review, no auto-promise) and
        // put the ticked list plus the note in the text the team reads, so every
        // claim stays pending our team's review.
        fd.append('issue_type', 'something_else');
        fd.append('feedback_text', 'Reported: ' + labels.join(', ') + '\n\nTheir note: ' + msg);
        if (prepared) fd.append('photo', prepared, prepared.name);

        renderProblemSending();
        submitForm(fd, function () {
          renderProblemConfirmation(labels);
        }, function (resp) {
          renderProblemFailed(resp, labels);
        });
      }).catch(function () {
        setBusy(submit, false, null, 'Send to our team');
        // The photo could not be decoded: flip the FIELD to its rejected state
        // too, so a stale "Ready to send" never contradicts the banner.
        if (photoStore.reject) photoStore.reject();
        errorSlot.appendChild(errorBanner('We could not read that photo. Please choose a JPG or PNG and try again.'));
      });
    });

    setScreen(
      screenScaffold({
        hero: 'card',
        rail: rail(3, true),
        back: backLink('Back', renderPathPicker),
        eyebrow: 'We will make it right',
        eyebrowMod: 'barley',
        heading: 'Tell us what you see',
        sub: 'Pick anything that fits, you can choose more than one.',
        body: [form]
      })
    );
  }

  function renderHubIssueBridgeUnavailable() {
    setPath('problem_claim');
    var helpBtn = CONFIG.whatsappNumber
      ? linkButton(
          'Message us on WhatsApp',
          whatsappLink('Hi Sprouties, I need help with order ' + orderName() + '.'),
          'ghost',
          ICON.whatsapp()
        )
      : accountHelpButton();
    setScreen(
      screenScaffold({
        hero: 'card',
        rail: rail(3, true),
        eyebrow: 'Your report is safe',
        eyebrowMod: 'barley',
        heading: 'We could not securely match this box',
        sub: CONFIG.whatsappNumber
          ? 'Nothing was submitted and no replacement was created. Try another order, or message us and we will check it with you.'
          : 'Nothing was submitted and no replacement was created. Try another order, or open Account and help for support.',
        body: [actions([helpBtn]), restartLink()]
      })
    );
  }

  function renderProblemSending() {
    setScreen(loadingScreen('barley', 'Sending to our team', 'Saving your note and photo. One moment.'));
  }

  // The report confirmation never promises a remedy. The claim is received and
  // pending our team's review, so the copy is the same no matter what the server
  // returns. The ticked issues come back as `labels`.
  function renderProblemConfirmation(labels) {
    setPath('problem_claim');
    root.setAttribute('data-hc-journey-complete', '1');

    var actionsList = [];
    if (CONFIG.whatsappNumber) {
      actionsList.push(
        linkButton('Message us on WhatsApp', whatsappLink('Hi Sprouties, I reported an issue with order ' + orderName() + '.'), 'ghost', ICON.whatsapp())
      );
    }

    var body = [actions(actionsList)];

    // A quiet "for your records" receipt: what was reported and the order. No
    // make-good line, since nothing is decided until our team reviews it.
    var capId = 'hc-report-receipt-cap';
    var reportText = (labels && labels.length) ? labels.join(', ') : 'Sent';
    var receipt = el('div', { class: 'hc-receipt hc-receipt--quiet', 'aria-live': 'off', role: 'group', 'aria-labelledby': capId }, [
      receiptRow('Your report', reportText),
      receiptRow('For order', orderName() + (boxLabel() ? ' · ' + grassFor(state.lineItem).label : ''))
    ]);
    body.push(el('p', { class: 'hc-receipt__cap', id: capId, text: 'For your records' }));
    body.push(receipt);
    body.push(checkAnotherLink());

    setScreen(
      doneScaffold({
        rail: rail(3, true),
        eyebrowMod: 'barley',
        eyebrow: 'Got it',
        title: 'This is with our team',
        lead: 'Our team reads every report and will follow up as soon as possible. Nothing more for you to do.',
        body: body
      })
    );
  }

  function renderProblemFailed(resp, labels) {
    setPath('problem_claim');
    if (resp && resp.status === 401) { renderExpired(); return; }

    var helpBtn = CONFIG.whatsappNumber
      ? linkButton('Send it to us on WhatsApp', whatsappLink('Hi Sprouties, I am reporting an issue with order ' + orderName() + '.'), 'ghost', ICON.whatsapp())
      : accountHelpButton();

    setScreen(
      screenScaffold({
        rail: rail(3, true),
        eyebrow: 'Still with you',
        eyebrowMod: 'barley',
        heading: 'That did not send just now',
        sub: CONFIG.whatsappNumber
          ? 'Your photo and note are safe. It did not reach our team that time, so try once more, or send it straight to us and we will pick it up.'
          : 'Your photo and note are safe. It did not reach our team that time, so try once more, or open Account and help for support.',
        body: [
          errorBanner('Everything you wrote is still here, ready to go.'),
          actions([
            retryButton('Try sending again', null, function () {
              renderProblemSending();
              submitForm(resp.fd, function () { renderProblemConfirmation(labels); }, function (r) { renderProblemFailed(r, labels); });
            }),
            helpBtn
          ])
        ]
      })
    );
  }

  // -------------------------------------------------------------------------
  // Step 4c: Not eating yet (coaching guide + actions)
  // -------------------------------------------------------------------------

  function renderNotEatingForm() {
    setPath('not_eating');
    var errorSlot = el('div', { class: 'hc-error-slot' });

    // Hope leads: the two real choices first, then a demoted two-tip guide, then
    // a quiet line for Chat / Pause. Actions map to the exact requested_action
    // enum: whatsapp, pause, guide_only.
    var primary = button('Give it a few more days', function () {
      logNotEating('guide_only', this, errorSlot, function () { renderEatingDone('guide_only'); });
    }, 'gold');

    var waCol = CONFIG.whatsappNumber
      ? button('Chat with us on WhatsApp', function () {
          var btn = this;
          logNotEating('whatsapp', btn, errorSlot, function () {
            openExternal(whatsappLink(
              'Hi Sprouties, my cat has not started nibbling the ' + (boxLabel() || 'box') +
              ' from order ' + orderName() + ' yet. Can you help?'
            ));
            renderEatingDone('whatsapp');
          });
        }, 'ghost')
      : null;

    var tips = [
      ['Place it where they relax', 'By a sunny window or a favorite perch, away from the litter and food bowls.'],
      ['For a particular eater, try oat', 'Oat is crisp grass for cats who are particular about what they eat.']
    ];
    var guide = el('ol', { class: 'hc-guide hc-guide--demoted', 'aria-label': 'A couple of things that help' });
    tips.forEach(function (t, i) {
      guide.appendChild(el('li', { class: 'hc-guide__step' }, [
        el('span', { class: 'hc-guide__num', 'aria-hidden': 'true', text: String(i + 1) }),
        el('span', { class: 'hc-guide__body' }, [
          el('span', { class: 'hc-guide__title', text: t[0] }),
          el('span', { class: 'hc-guide__text', text: t[1] })
        ])
      ]));
    });

    // Quiet text row: Chat (whatsapp) · Pause (recharge portal).
    var textRow = el('div', { class: 'hc-textrow' });
    if (CONFIG.whatsappNumber) {
      var chat = el('button', { type: 'button', class: 'hc-textlink' });
      chat.appendChild(ICON.whatsapp());
      chat.appendChild(document.createTextNode('Chat with us'));
      chat.addEventListener('click', function () {
        var btn = this;
        logNotEating('whatsapp', btn, errorSlot, function () {
          openExternal(whatsappLink('Hi Sprouties, can you help my cat get nibbling the ' + (boxLabel() || 'box') + '?'));
          renderEatingDone('whatsapp');
        });
      });
      textRow.appendChild(chat);
    }
    if (CONFIG.whatsappNumber && CONFIG.rechargePortalUrl) {
      textRow.appendChild(el('span', { class: 'hc-textrow__sep', 'aria-hidden': 'true' }));
    }
    if (CONFIG.rechargePortalUrl) {
      var pause = el('button', { type: 'button', class: 'hc-textlink', text: 'Pause my plan' });
      pause.addEventListener('click', function () {
        var btn = this;
        logNotEating('pause', btn, errorSlot, function () {
          openExternal(CONFIG.rechargePortalUrl);
          renderEatingDone('pause');
        });
      });
      textRow.appendChild(pause);
    }

    setScreen(
      screenScaffold({
        hero: 'card',
        rail: rail(3, true),
        back: backLink('Back', renderPathPicker),
        eyebrow: 'A gentle start',
        eyebrowMod: 'wheat',
        heading: 'This is normal, and it usually turns around',
        sub: '<strong>Most cats come around in a few days,</strong> and a few small changes usually help. Pick whatever suits you next.',
        body: [
          actions([waCol, primary]),
          guide,
          textRow.childNodes.length ? textRow : null,
          errorSlot,
          restartLink()
        ]
      })
    );
  }

  // Log the not_eating request once per visit with a requested_action, then run
  // the follow-up. A flag stops duplicate logs if more than one action is tapped.
  var notEatingLogged = false;
  function logNotEating(requestedAction, btn, errorSlot, after) {
    if (notEatingLogged) { after(); return; }

    var idleLabel = btn ? btn.textContent : '';
    var isBtn = btn && btn.classList && btn.classList.contains('hc-btn');
    if (isBtn) setBusy(btn, true, 'One moment', idleLabel);

    var fd = baseFormData('not_eating');
    fd.append('requested_action', requestedAction);

    postSubmission(fd)
      .then(function (resp) {
        if (isBtn) setBusy(btn, false, 'One moment', idleLabel);
        if (resp.ok && resp.body && (resp.body.submission_id || resp.body.status)) {
          notEatingLogged = true; after(); return;
        }
        if (resp.status === 409) { notEatingLogged = true; after(); return; }
        // Even if logging fails, never block the customer from getting help.
        if (errorSlot) {
          clear(errorSlot);
          errorSlot.appendChild(errorBanner('We could not log that just now, but you can still reach us below.'));
        }
        after();
      })
      .catch(function () {
        if (isBtn) setBusy(btn, false, 'One moment', idleLabel);
        after();
      });
  }

  function renderEatingDone(action) {
    setPath('not_eating');
    root.setAttribute('data-hc-journey-complete', '1');

    if (action === 'whatsapp') {
      setScreen(
        doneScaffold({
          rail: rail(3, true),
          seal: 'wheat',
          sealMotion: 'rise',
          sealIcon: ICON.sprout(),
          eyebrowMod: 'wheat',
          eyebrow: 'With you',
          title: 'We have opened WhatsApp',
          text: 'Chat with us there and we will help you coax the first bite. Give the guide a few days too, most cats come around with a little time and the right spot.',
          body: [
            actions([CONFIG.rechargePortalUrl ? linkButton('Manage my plan', CONFIG.rechargePortalUrl, 'ghost') : null]),
            checkAnotherLink()
          ]
        })
      );
      return;
    }

    if (action === 'pause') {
      setScreen(
        doneScaffold({
          rail: rail(3, true),
          seal: 'wheat',
          sealMotion: 'rise',
          sealIcon: ICON.pause(),
          eyebrowMod: 'wheat',
          eyebrow: 'Paused',
          title: 'Pausing is one tap away',
          text: 'We have opened your subscription portal so you can pause. No box and no charge until you are ready, then start again in one tap whenever your cat is keen.',
          body: [
            actions([CONFIG.rechargePortalUrl ? linkButton('Open my plan', CONFIG.rechargePortalUrl, 'ghost') : null]),
            checkAnotherLink()
          ]
        })
      );
      return;
    }

    // guide_only
    var waBtn = CONFIG.whatsappNumber
      ? linkButton('Message us anytime', whatsappLink('Hi Sprouties, can you help my cat get nibbling?'), 'ghost', ICON.whatsapp())
      : null;
    var planRow = CONFIG.rechargePortalUrl
      ? el('div', { class: 'hc-textrow' }, [planLink()])
      : null;

    setScreen(
      doneScaffold({
        rail: rail(3, true),
        seal: 'wheat',
        sealMotion: 'rise',
        sealIcon: ICON.sprout(),
        eyebrowMod: 'wheat',
        eyebrow: 'With you',
        title: 'Your guide is saved',
        text: 'Give it a few days. Most cats come around with a little time and the right spot, and your tips are here whenever you want them.',
        body: [
          actions([waBtn]),
          planRow,
          doneNote('We will check in in a few days to see how your cat is getting on.'),
          checkAnotherLink()
        ]
      })
    );
  }

  function planLink() {
    var a = el('a', { class: 'hc-textlink', href: CONFIG.rechargePortalUrl, target: '_blank', rel: 'noopener', text: 'Manage my plan' });
    return a;
  }

  // -------------------------------------------------------------------------
  // Shared: expired token
  // -------------------------------------------------------------------------

  function renderExpired() {
    setPath(null);
    setScreen(
      screenScaffold({
        rail: rail(1, false),
        eyebrow: 'Almost there',
        heading: 'This link has expired',
        sub: 'For your security, the link timed out. Look up your order again and we will take you right back to it.',
        body: [
          actions([button('Look up my order again', resetWizard, null)])
        ]
      })
    );
  }

  // -------------------------------------------------------------------------
  // Loading screen
  // -------------------------------------------------------------------------

  function loadingScreen(grass, text, sub) {
    var spinner = el('span', { class: 'hc-spinner' + (grass ? ' hc-spinner--' + grass : '') });
    var loading = el('div', { class: 'hc-loading', role: 'status', 'aria-live': 'polite' }, [
      spinner,
      el('span', { class: 'hc-loading__text', text: text }),
      el('span', { class: 'hc-loading__sub', text: sub })
    ]);
    var screen = el('div', { class: 'hc-screen' }, [loading]);
    return el('div', { class: 'hc-wizard' }, [brandbar(), screen]);
  }

  // -------------------------------------------------------------------------
  // Submit plumbing
  // -------------------------------------------------------------------------

  // The idempotency_key is minted once per path and reused across every retry,
  // so a customer who taps submit again after a slow or failed response sends the
  // SAME key. The server's idempotency lock returns the prior result instead of
  // creating a second make-good. Cleared on resetWizard().
  function idempotencyKeyFor(path) {
    if (!state.idempotencyKeys[path]) {
      state.idempotencyKeys[path] = uuid();
    }
    return state.idempotencyKeys[path];
  }

  function baseFormData(path) {
    var fd = new FormData();
    fd.append('token', state.token || '');
    fd.append('line_item_id', state.lineItem ? state.lineItem.id : '');
    fd.append('path', path);
    fd.append('idempotency_key', idempotencyKeyFor(path));
    fd.append('website', ''); // honeypot, always empty for real customers
    return fd;
  }

  // onSuccess(body), onFail(resp) where resp carries { status, body, fd } so a
  // failure screen can retry with the SAME FormData (and same idempotency_key).
  function submitForm(fd, onSuccess, onFail) {
    if (state.submitting) return;
    state.submitting = true;

    postSubmission(fd)
      .then(function (resp) {
        state.submitting = false;
        if (resp.ok && resp.body && (resp.body.submission_id || resp.body.status)) {
          onSuccess(resp.body);
          return;
        }
        // 409 duplicate returns the prior submission: still a success.
        if (resp.status === 409 && resp.body) {
          onSuccess(resp.body);
          return;
        }
        onFail({ status: resp.status, body: resp.body, fd: fd, rating: ratingFromFd(fd) });
      })
      .catch(function () {
        state.submitting = false;
        onFail({ status: 0, body: {}, fd: fd, rating: ratingFromFd(fd) });
      });
  }

  function ratingFromFd(fd) {
    var r = fd.get ? fd.get('rating') : null;
    return r ? parseInt(r, 10) : 0;
  }

  function retryButton(label, variant, onClick) {
    return button(label, onClick, variant, ICON.retry());
  }

  // -------------------------------------------------------------------------
  // Shared bits
  // -------------------------------------------------------------------------

  function orderName() {
    return (state.order && state.order.name) || '';
  }

  function firstName() {
    var name = state.order && state.order.first_name;
    return (name && name !== 'there') ? name : '';
  }

  // "Found it" -> "Found it, Layla"; tail lets us append "?" before the name comma.
  function greetingHeading(base, tail) {
    var n = firstName();
    if (tail === '?') {
      return n ? base + ', ' + n + '?' : base + '?';
    }
    return n ? base + ', ' + n : base;
  }

  function placedLine() {
    var placed = state.order && (state.order.placed_at || state.order.placed);
    if (!placed) return 'Recent order';
    var d = new Date(placed);
    if (isNaN(d.getTime())) return String(placed);
    try {
      return 'Placed ' + d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    } catch (e) {
      return 'Placed ' + d.getDate();
    }
  }

  function boxLabel() {
    if (!state.lineItem) return '';
    var g = GRASS[state.lineItem.grass];
    if (g && g !== GRASS.other && g !== GRASS.mixed) return g.label.toLowerCase();
    return '';
  }

  function isQualityIssue(id) {
    return QUALITY_ISSUES.indexOf(id) !== -1;
  }

  function issueLabelFor(id) {
    for (var i = 0; i < ISSUE_GROUPS.length; i++) {
      var opts = ISSUE_GROUPS[i].options;
      for (var j = 0; j < opts.length; j++) {
        if (opts[j].id === id) return opts[j].label;
      }
    }
    return 'Reported';
  }

  function receiptRow(k, v, withCheck) {
    var val = el('span', { class: 'hc-receipt__v' });
    if (withCheck) val.appendChild(ICON.checkBold());
    val.appendChild(document.createTextNode(v));
    return el('div', { class: 'hc-receipt__row' }, [
      el('span', { class: 'hc-receipt__k', text: k }),
      val
    ]);
  }

  function resetWizard() {
    root.removeAttribute('data-hc-journey-complete');
    state.autoEntered = false;
    state.token = null;
    state.order = null;
    state.lineItem = null;
    state.path = null;
    state.submitting = false;
    state.idempotencyKeys = {}; // new lookup/order -> fresh idempotent submissions
    notEatingLogged = false;
    renderLookup();
  }

  // Hub close hygiene: keep unfinished drafts, but completed journeys should
  // reopen at the verified box choice rather than an old success receipt.
  root.addEventListener('wh:happy-cats-reset-completed', function () {
    if (root.getAttribute('data-hc-journey-complete') !== '1') return;
    root.removeAttribute('data-hc-journey-complete');
    state.path = null;
    state.submitting = false;
    state.idempotencyKeys = {};
    notEatingLogged = false;
    if (state.token && state.order && state.lineItem) renderPathPicker();
    else if (CONFIG.autoOrder && CONFIG.autoContact) renderAutoLookup();
    else renderLookup();
  });

  // -------------------------------------------------------------------------
  // Boot
  // -------------------------------------------------------------------------

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount);
  } else {
    mount();
  }
})();
